import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, CheckCircle2, Package, Truck, MapPin, Home } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STEPS = [
  ["Awaiting Fulfillment", "Order Confirmed", CheckCircle2],
  ["Supplier Order Created", "Processing", Package],
  ["Shipped", "Shipped", Truck],
  ["In Transit", "In Transit", MapPin],
  ["Delivered", "Delivered", Home],
];

export default function Track() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { format } = useCurrency();
  const [orderNum, setOrderNum] = useState(params.get("order") || "");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (email) sp.set("email", email);
      const { data } = await api.get(`/orders/track/${orderNum}?${sp.toString()}`);
      setResult(data);
    } catch (e) { toast.error(apiErr(e)); setResult(null); } finally { setLoading(false); }
  };

  useEffect(() => { if (params.get("order") && user) lookup(); /* eslint-disable-next-line */ }, [user]);

  const order = result?.order;
  const flow = ["Awaiting Fulfillment", "Supplier Order Created", "Processing", "Shipped", "In Transit", "Delivered"];
  const currentIdx = order ? Math.max(0, flow.indexOf(order.status)) : 0;

  return (
    <div className="max-w-2xl mx-auto px-5 py-16">
      <h1 className="font-display text-4xl font-bold tracking-tight text-center">Track Your Order</h1>
      <form onSubmit={lookup} className="mt-8 space-y-3">
        <Input placeholder="Order number (e.g. ORD-1001)" value={orderNum} onChange={(e) => setOrderNum(e.target.value)} required data-testid="track-order-number" />
        {!user && <Input placeholder="Email used at checkout" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="track-email" />}
        <Button type="submit" disabled={loading} className="w-full rounded-full h-11" data-testid="track-submit"><Search size={15} className="mr-2" />{loading ? "Searching..." : "Track order"}</Button>
      </form>

      {order && (
        <div className="mt-12" data-testid="track-result">
          <div className="flex justify-between items-center mb-8">
            <div><p className="font-mono font-bold text-lg">{order.order_number}</p><p className="text-xs text-zinc-500">{new Date(order.created_at).toLocaleDateString()}</p></div>
            <span className="font-semibold">{format(order.total)}</span>
          </div>

          {["Cancelled", "Refunded"].includes(order.status) ? (
            <div className="bg-red-50 text-red-700 rounded-xl p-6 text-center font-medium">{order.status}</div>
          ) : (
            <div className="space-y-0">
              {STEPS.map(([key], i) => {
                const done = currentIdx >= flow.indexOf(key);
                const [, label, Icon] = STEPS[i];
                return (
                  <div key={key} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${done ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-400"}`}><Icon size={17} /></div>
                      {i < STEPS.length - 1 && <div className={`w-0.5 h-10 ${done ? "bg-zinc-900" : "bg-zinc-200"}`} />}
                    </div>
                    <div className="pt-2"><p className={`text-sm font-semibold ${done ? "text-zinc-900" : "text-zinc-400"}`}>{label}</p></div>
                  </div>
                );
              })}
            </div>
          )}

          {result.tracking_events?.length > 0 && (
            <div className="mt-8 bg-zinc-50 rounded-xl p-5">
              <h3 className="font-semibold text-sm mb-3">Tracking Updates</h3>
              {result.tracking_events.map((t) => (
                <div key={t.id} className="flex justify-between text-xs py-1.5 border-b border-zinc-100 last:border-0">
                  <span>{t.status}{t.tracking_number && ` · ${t.courier} ${t.tracking_number}`}</span>
                  <span className="text-zinc-400">{new Date(t.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 border border-zinc-200 rounded-xl p-5 space-y-3">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex gap-3 items-center">
                <img src={i.image} alt="" className="w-12 h-14 object-cover rounded-md" />
                <div className="flex-1 text-sm"><p className="font-medium line-clamp-1">{i.title}</p><p className="text-xs text-zinc-500">Qty {i.quantity}</p></div>
                <span className="text-sm">{format(i.selling_price * i.quantity)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
